/**
 * Which workspace events invalidate a campaign's reviewed snapshot.
 *
 * The snapshot behind the budget review and the signed submission has to describe
 * the workspace as it stands, or the submit guard refuses a submission that no
 * longer matches its own review. That guard is right; keeping this list complete is
 * what stops it firing on the reviewer.
 *
 * Its own module, not constants inside the panel: a test that asserts the list
 * covers what the baseline reads must not drag React and MUI into the no-DOM test
 * chunk, which exhausts the heap (see #137).
 *
 * @module ui/dialog/WorkflowPane/campaignReviewEvents
 */

/**
 * The workspace gained or lost a protein or a compound. Read directly — these are
 * discrete user-driven changes, not bursts.
 */
export const CAMPAIGN_ALIAS_REFRESH_EVENTS = [
    'compoundsUpdated',
    'deleteCompound',
    'dataLoaded',
    'proteinLoaded',
    'proteinsChanged',
];

/**
 * The same proteins and compounds, but derived data about them has arrived.
 *
 * This matters because the signed baseline is not just an inventory. It carries
 * `initialDockingScoreIds` — a compound with a finite affinity or pose score — and
 * `fragmentAvailability`. Both land asynchronously, after the load events above
 * have already fired.
 *
 * Missing them is what made the first Start on a freshly loaded workspace fail. The
 * reviewed snapshot was taken at `proteinLoaded`, before the crystal ligand's
 * interaction score arrived; by the time the reviewer clicked, the live workspace
 * had a score the reviewed baseline did not. The rejection path then refreshed the
 * snapshot, which is why pressing Start a second time worked.
 *
 * Debounced by the panel, unlike the list above: `energyCalc` fires on every
 * minimize and re-score, so during a run it arrives in bursts.
 */
export const CAMPAIGN_DERIVED_DATA_EVENTS = [
    'energyCalc',
    'redisplayFragments',
];
